import { SIZING_FRACTION } from '@postflop/schema';
import { riskPremium } from '../strategy/icm';
import { realizationFactor } from './realization';
/** Below this bb gap the model treats two actions as tied - its own noise floor (§7.2). */
export const DEFAULT_NOISE_FLOOR_BB = 0.5;
/** Pot fraction a betting intent means; jam/min fall back to a documented default fraction here (the
 *  precise min-raise / all-in chip amount is bet-math's job - for EV ranking a fraction is enough). */
const betFraction = (intent) => {
    if (!intent)
        return 2 / 3;
    if (intent === 'min')
        return 0.25;
    if (intent === 'jam')
        return 1.0; // jam EV is handled by evAllIn; this is only a fallback.
    return SIZING_FRACTION[intent];
};
/** EV of a bet of fraction `f`·pot (§7.2): F·P + (1−F)·[E_vc·(P + 2b) − b], in the same units as pot. */
const evBet = (pot, f, foldProb, equityVsContinue) => {
    const b = f * pot;
    return foldProb * pot + (1 - foldProb) * (equityVsContinue * (pot + 2 * b) - b);
};
/** EV of a call (§7.2): E·(P + b) − b, with E realization-discounted by R. */
const evCall = (pot, toCall, equity, realization) => equity * realization * (pot + toCall) - toCall;
/** EV of a check (§7.2): realize equity over the current pot, discounted by R. Reference = 0 pot won
 *  is not right - a check still realizes equity in the pot already built; we credit E·R·P as the
 *  showdown-value estimate of checking behind/into a check. */
const evCheck = (pot, equity, realization) => equity * realization * pot;
/**
 * Is this a spot where one-street EV cannot honestly rank the top actions? Deep-SPR turn decisions
 * with a live draw carry heavy reverse-implied / multi-street considerations one street of algebra
 * cannot see (§7.2). Coarse and documented - the caller may override via `EvInputs.multiStreetDependent`.
 */
export const isMultiStreetDependent = (ctx) => {
    const deep = ctx.sprBucket === '6to13' || ctx.sprBucket === 'over13';
    const hasDraw = ctx.hero.drawFlags.length > 0 || ctx.hero.handClass === 'weak_draw' || ctx.hero.handClass === 'strong_draw';
    return ctx.street === 'turn' && deep && hasDraw;
};
/** The candidate action set for a spot: facing a bet → {fold, call, raise}; else → {check, bet}. The
 *  recommendation's own sizing intent is used for the betting candidate so EV compares like sizes.
 *  Jam is NOT auto-added: with a single injected fold-prob it would spuriously dominate every spot
 *  (F·P is constant while equity realization grows with size). A jam is scored only when the USER or
 *  the recommendation actually chose it - the dedicated ICM/all-in path (`evAllIn`) prices those. */
const candidateSet = (ctx, betIntent) => ctx.price.toCall > 0
    ? [{ action: 'fold' }, { action: 'call' }, { action: 'raise', sizingIntent: betIntent ?? 'threeQuarters' }]
    : [{ action: 'check' }, { action: 'bet', sizingIntent: betIntent ?? 'twoThirds' }];
/**
 * Tournament all-in EV, in bb, via the risk-premium shifted-breakeven model (§6.5, §7.2).
 *
 * The chip-EV of a call vs folding is linear in equity and crosses zero at the chip breakeven pChip:
 *   chipEV(call) = (E − pChip)·S,  where S = 2·atRisk + dead  (the bb swing across win↔lose)
 * ICM only shifts the breakeven by the risk premium rp = riskPremium(spot):
 *   icmEV(call) = (E − (pChip + rp))·S
 * so a call that is +chip-EV (E > pChip) but −$EV (E < pChip + rp) yields a POSITIVE evLoss vs folding
 * and grades down - exactly the tournament case the done-criteria pins. Fold is the reference (EV 0).
 */
const evAllIn = (spot, equity, tournament) => {
    const dead = spot.deadMoney ?? 0;
    const swing = 2 * spot.atRisk + dead; // bb swing between calling-and-winning vs calling-and-losing.
    const pChip = spot.atRisk / swing;
    const breakeven = tournament ? pChip + riskPremium(spot) : pChip;
    return { call: (equity - breakeven) * swing, fold: 0 };
};
/**
 * Estimate the one-street EV of every candidate action and the loss of the chosen one. Pure function
 * of (ctx, chosen, inputs) - same inputs, same estimate, every time.
 */
export const estimateEv = (ctx, chosen, inputs = {}) => {
    const pot = ctx.price.pot;
    const toCall = ctx.price.toCall;
    const equity = inputs.equity ?? ctx.hero.equity;
    const equityVsContinue = inputs.equityVsContinue ?? equity;
    const foldProb = inputs.foldProb ?? 0.4;
    const realization = inputs.realization ?? realizationFactor(ctx.hero.handClass, ctx.positionClass, ctx.hero.drawFlags);
    const noiseFloor = inputs.noiseFloorBB ?? DEFAULT_NOISE_FLOOR_BB;
    // ── tournament all-in path: EV priced through ICM ──────────────────────────────
    const tournament = inputs.tournament ?? true; // an all-in spot is priced through ICM by default.
    let perAction;
    if (inputs.allIn) {
        const { call, fold } = evAllIn(inputs.allIn, equity, tournament);
        // an all-in decision collapses to call vs fold (jam-into-caller == call-the-jam here).
        perAction = [
            { action: 'fold', evBB: fold },
            { action: 'call', evBB: call },
            { action: 'jam', sizingIntent: 'jam', evBB: call },
        ];
    }
    else {
        perAction = candidateSet(ctx, chosen.sizingIntent).map((c) => {
            switch (c.action) {
                case 'fold':
                    return { ...c, evBB: 0 };
                case 'check':
                    return { ...c, evBB: evCheck(pot, equity, realization) };
                case 'call':
                    return { ...c, evBB: evCall(pot, toCall, equity, realization) };
                case 'bet':
                case 'raise':
                    return { ...c, evBB: evBet(pot, betFraction(c.sizingIntent), foldProb, equityVsContinue) };
                case 'jam':
                    return { ...c, evBB: evBet(pot, 1.0, foldProb, equityVsContinue) };
                default:
                    return { ...c, evBB: 0 };
            }
        });
    }
    // score the chosen action (it may not be in the derived set - score it directly so grading always
    // has a number).
    const chosenScore = scoreChosen(chosen, { pot, toCall, equity, equityVsContinue, foldProb, realization }, inputs);
    if (!perAction.some((p) => p.action === chosen.action && p.sizingIntent === chosen.sizingIntent)) {
        perAction = [...perAction, chosenScore];
    }
    const sorted = [...perAction].sort((a, b) => b.evBB - a.evBB);
    const best = sorted[0];
    const second = sorted[1];
    const multiStreet = inputs.multiStreetDependent ?? isMultiStreetDependent(ctx);
    if (multiStreet && second && best.evBB - second.evBB < noiseFloor) {
        return {
            perAction,
            best,
            chosen: chosenScore,
            evLossBB: null,
            abstained: true,
            abstainReason: 'multi-street-dependent spot: top actions within the one-street model\'s noise floor, graded on rule/principle compliance',
        };
    }
    return {
        perAction,
        best,
        chosen: chosenScore,
        evLossBB: Math.max(0, best.evBB - chosenScore.evBB),
        abstained: false,
    };
};
/** Score a single chosen action directly (used when it is outside the derived candidate set). */
const scoreChosen = (chosen, q, inputs) => {
    if (inputs.allIn) {
        const { call, fold } = evAllIn(inputs.allIn, q.equity, inputs.tournament ?? true);
        const ev = chosen.action === 'fold' ? fold : call;
        return { ...chosen, evBB: ev };
    }
    switch (chosen.action) {
        case 'fold':
            return { ...chosen, evBB: 0 };
        case 'check':
            return { ...chosen, evBB: evCheck(q.pot, q.equity, q.realization) };
        case 'call':
            return { ...chosen, evBB: evCall(q.pot, q.toCall, q.equity, q.realization) };
        case 'bet':
        case 'raise':
            return { ...chosen, evBB: evBet(q.pot, betFraction(chosen.sizingIntent), q.foldProb, q.equityVsContinue) };
        case 'jam':
            return { ...chosen, evBB: evBet(q.pot, 1.0, q.foldProb, q.equityVsContinue) };
        default:
            return { ...chosen, evBB: 0 };
    }
};
//# sourceMappingURL=ev.js.map